<!-- app-subdomain-creators\src\Components\NewsfeedCreatorPostMenu.vue -->

<template>
  <div ref="menuRoot" class="relative inline-block">
    <!-- Ellipsis button -->
    <button
      ref="btnEl"
      type="button"
      class="flex items-center justify-center w-10 h-10 text-[26px] rounded-full hover:bg-zinc-100 text-zinc-500 font-bold"
      @click.stop="toggleMenu"
    >
      <span class="sr-only">More</span>
      ⋯
    </button>

    <!-- ✅ Teleport dropdown OUT of virtual-scroller stacking contexts -->
    <teleport to="body">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9998]"
        @click="closeMenu"
      >
        <!-- The actual menu -->
        <div
          ref="dropdownEl"
          class="fixed w-72 bg-white rounded-2xl
                 shadow-[0_0_30px_rgba(15,23,42,0.30)]
                 border border-zinc-200/80 z-[9999]"
          :style="dropdownStyle"
          @click.stop
        >
          <!-- little arrow -->
          <div
            class="pointer-events-none absolute overflow-hidden"
            :style="arrowStyle"
          >
            <img
              :src="menuArrow"
              alt=""
              class="w-8"
              :class="menuPlacement === 'top' ? 'rotate-180' : ''"
            />
          </div>

          <div class="py-4 max-h-[400px] overflow-y-auto">
            <!-- 🔹 Scheduling controls (ONLY for scheduled posts) -->
            <template v-if="isScheduled">
              <button
                type="button"
                class="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-100
                       text-[15px] text-zinc-900 text-left font-medium"
                @click="handleClick('reschedule-post')"
              >
                <CalendarDaysIcon class="w-5 h-5 text-zinc-900" />
                <span>Reschedule post</span>
              </button>

              <button
                type="button"
                class="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-100
                       text-[15px] text-zinc-900 text-left font-medium"
                @click="handleClick('post-now')"
              >
                <PencilSquareIcon class="w-5 h-5 text-zinc-900" />
                <span>Post now</span>
              </button>

              <div class="my-1 border-t border-zinc-200"></div>
            </template>

            <!-- Save / Unsave post -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-100 text-[15px] text-zinc-900 text-left font-medium"
              @click="handleClick(isSaved ? 'unsave-post' : 'save-post')"
            >
              <img :src="saveIcon" alt="" class="w-5 h-5" />
              <div class="flex flex-col items-start">
                <span>{{ isSaved ? 'Unsave post' : 'Save post' }}</span>
                <span v-if="isSaved && inSavedTab" class="text-xs text-zinc-500">
                  Remove this from your saved items
                </span>
              </div>
            </button>

            <!-- Pin / Unpin to featured -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-100 text-[15px] text-zinc-900 text-left font-medium"
              @click="handleClick('pin-post')"
            >
              <img :src="pinIcon" alt="" class="w-5 h-5" />
              <span>{{ isPinned ? 'Unpin from Featured' : 'Pin to Featured' }}</span>
            </button>

            <div class="my-1 border-t border-zinc-200"></div>

            <!-- Copy post link -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-100 text-[15px] text-zinc-900 text-left font-medium"
              @click="handleClick('copy-post-link')"
            >
              <LinkIcon class="w-5 h-5 text-zinc-900" />
              <span>Copy post link</span>
            </button>

            <div class="my-1 border-t border-zinc-200"></div>

            <!-- Edit post -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-100 text-[15px] text-zinc-900 text-left font-medium"
              @click="handleClick('edit-post')"
            >
              <img :src="editIcon" alt="" class="w-5 h-5" />
              <span>Edit post</span>
            </button>

            <!-- Delete post -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-red-50 text-[15px] text-zinc-900 text-left font-medium"
              @click="handleClick('delete-post')"
            >
              <img :src="deleteIcon" alt="" class="w-5 h-5" />
              <span>Delete post</span>
            </button>

            <div class="my-1 border-t border-zinc-200"></div>

            <!-- Turn off commenting -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-100 text-[15px] text-zinc-900 text-left font-medium"
              @click="handleClick('toggle-commenting')"
            >
              <img :src="commentOffIcon" alt="" class="w-5 h-5" />
              <span>Turn off commenting</span>
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { CalendarDaysIcon, PencilSquareIcon } from '@heroicons/vue/24/outline';
import { LinkIcon } from '@heroicons/vue/24/solid';

const menuArrow = new URL(
  '@/assets/media/images/newsfeed-menu-little-arrow.svg',
  import.meta.url
).href;

const props = defineProps({
  postId: { type: [Number, String], required: true },
  isSaved: { type: Boolean, default: false },
  isPinned: { type: Boolean, default: false },
  inSavedTab: { type: Boolean, default: false },
  isScheduled: { type: Boolean, default: false },
});

const emit = defineEmits([
  'save-post',
  'unsave-post',
  'copy-post-link',
  'pin-post',
  'edit-post',
  'delete-post',
  'toggle-commenting',
  'reschedule-post',
  'post-now',
]);

const saveIcon = new URL('@/assets/media/images/newsfeed-save-icon.svg', import.meta.url).href;
const pinIcon = new URL('@/assets/media/images/newsfeed-pin-icon.svg', import.meta.url).href;
const editIcon = new URL('@/assets/media/images/newsfeed-edit-icon.svg', import.meta.url).href;
const deleteIcon = new URL('@/assets/media/images/newsfeed-delete-trash-icon.svg', import.meta.url).href;
const commentOffIcon = new URL('@/assets/media/images/newsfeed-comment-off-icon.svg', import.meta.url).href;

const isOpen = ref(false);
const menuRoot = ref(null);
const btnEl = ref(null);
const dropdownEl = ref(null);

const menuPlacement = ref('bottom'); // 'bottom' | 'top'
const dropdownStyle = ref({ top: '0px', left: '0px' });
const arrowStyle = ref({ top: '-14px', right: '0px' });

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const updatePosition = async () => {
  await nextTick();

  const btn = btnEl.value;
  const dd = dropdownEl.value;
  if (!btn || !dd) return;

  const btnRect = btn.getBoundingClientRect();
  const ddRect = dd.getBoundingClientRect();

  const viewportW = window.innerWidth || document.documentElement.clientWidth;
  const viewportH = window.innerHeight || document.documentElement.clientHeight;

  const spaceBelow = viewportH - btnRect.bottom;
  const spaceAbove = btnRect.top;

  const needed = ddRect.height + 12;
  const showTop = spaceBelow < needed && spaceAbove > spaceBelow;
  menuPlacement.value = showTop ? 'top' : 'bottom';

  // Position: align right edge of menu with button (like your right-3)
  const menuWidth = ddRect.width || 288; // w-72
  let left = btnRect.right - menuWidth;
  left = clamp(left, 12, viewportW - menuWidth - 12);

  let top;
  if (menuPlacement.value === 'top') {
    top = btnRect.top - ddRect.height - 10;
  } else {
    top = btnRect.bottom + 10;
  }

  // Keep on-screen vertically too
  top = clamp(top, 12, viewportH - ddRect.height - 12);

  dropdownStyle.value = {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
  };

  // Arrow: keep it near the right edge to match your current look
  arrowStyle.value =
    menuPlacement.value === 'top'
      ? { bottom: '-17px', right: '-2px' }
      : { top: '-14px', right: '-0.5px' };
};

const toggleMenu = async () => {
  if (isOpen.value) {
    isOpen.value = false;
    return;
  }

  isOpen.value = true;
  await updatePosition();
};

const closeMenu = () => {
  isOpen.value = false;
};

const handleClick = (action) => {
  emit(action, { postId: props.postId });
  closeMenu();
};

// Update position while open (scroll inside any container too)
let raf = null;
const onAnyScrollOrResize = () => {
  if (!isOpen.value) return;
  if (raf) cancelAnimationFrame(raf);
  raf = requestAnimationFrame(() => updatePosition());
};

onMounted(() => {
  window.addEventListener('resize', onAnyScrollOrResize, { passive: true });
  window.addEventListener('scroll', onAnyScrollOrResize, true); // capture scrolls in nested containers
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onAnyScrollOrResize);
  window.removeEventListener('scroll', onAnyScrollOrResize, true);
  if (raf) cancelAnimationFrame(raf);
});
</script>






